import React, { useState, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Editor from "@monaco-editor/react";
import { createPageBlock, updatePageBlock, getPageBlock, getPageBlocksList, PageBlock } from "../../common/api/pageApi";
import Loader from "../../shared/Ui/Loader/Loader";
import { useNavigate } from "react-router-dom";

interface ComponentBlock {
  id: string;
  name: string;
  html: string;
  css: string;
  js: string;
  json?: string; // JSON структура компонента
}

interface PageEditorV2Props {
  blockId?: number | string;
}

// Пример JSON структуры для компонента
const EXAMPLE_JSON = {
  component: {
    name: "Hero Section",
    description: "Главный блок с заголовком и кнопкой",
    html: "<div class=\"hero\"><h1>Заголовок</h1><button>Кнопка</button></div>",
    css: ".hero { padding: 40px; text-align: center; } h1 { font-size: 32px; }",
    js: "console.log('Hero loaded');"
  }
};

function PageEditorV2({ blockId }: PageEditorV2Props) {
  const navigate = useNavigate();
  const [components, setComponents] = useState<ComponentBlock[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<ComponentBlock | null>(null);
  const [jsonInput, setJsonInput] = useState("");
  const [showJsonExample, setShowJsonExample] = useState(false);
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedBlockId, setSavedBlockId] = useState<number | string | null>(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [componentJson, setComponentJson] = useState("");
  const [jsonEditorHeight, setJsonEditorHeight] = useState(250);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const jsonEditorContainerRef = useRef<HTMLDivElement>(null);

  // Обработка изменения размера JSON редактора
  useEffect(() => {
    if (!jsonEditorContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) {
          setJsonEditorHeight(newHeight);
        }
      }
    });

    resizeObserver.observe(jsonEditorContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedComponent]);

  // Подавление ошибки ResizeObserver - устанавливаем сразу при монтировании
  useEffect(() => {
    const originalError = window.onerror;
    
    // Подавляем ошибку ResizeObserver через window.onerror
    window.onerror = (message, source, lineno, colno, error) => {
      if (
        (typeof message === 'string' && message.includes('ResizeObserver loop')) ||
        (error && error.message && error.message.includes('ResizeObserver'))
      ) {
        return true; // Подавляем ошибку
      }
      if (originalError) {
        return originalError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Подавляем ошибку ResizeObserver через addEventListener для error
    const handleError = (event: ErrorEvent) => {
      if (
        (event.message && event.message.includes('ResizeObserver loop')) ||
        (event.error && event.error.message && event.error.message.includes('ResizeObserver'))
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Подавляем ошибку ResizeObserver через addEventListener для unhandledrejection
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        (event.reason && typeof event.reason === 'string' && event.reason.includes('ResizeObserver')) ||
        (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver'))
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.onerror = originalError;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Загрузка существующего блока
  useEffect(() => {
    if (blockId) {
      loadBlock(blockId);
    } else {
      // Загружаем список всех блоков
      loadBlocksList();
    }
  }, [blockId]);

  const loadBlock = async (id: number | string) => {
    setIsLoading(true);
    try {
      const block = await getPageBlock(id);
      setName(block.name);
      setSavedBlockId(block.id);
      
      // Парсим блок в компоненты
      let loadedComponents: ComponentBlock[] = [];
      try {
        const parsed = JSON.parse(block.html || "[]");
        if (Array.isArray(parsed)) {
          loadedComponents = parsed;
        } else {
          // Если это не массив, создаем один компонент
          loadedComponents = [{
            id: "1",
            name: block.name,
            html: block.html,
            css: block.css,
            js: block.js,
          }];
        }
      } catch {
        // Если не JSON, создаем один компонент из блока
        loadedComponents = [{
          id: "1",
          name: block.name,
          html: block.html,
          css: block.css,
          js: block.js,
        }];
      }
      
      setComponents(loadedComponents);
      
      // Автоматически выбираем первый блок
      if (loadedComponents.length > 0) {
        setSelectedComponent(loadedComponents[0]);
      }
    } catch (error) {
      console.error("Ошибка загрузки блока:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBlocksList = async () => {
    try {
      const blocks = await getPageBlocksList();
      // Преобразуем блоки в компоненты
      const comps = blocks.map((block, index) => ({
        id: String(block.id),
        name: block.name,
        html: block.html,
        css: block.css,
        js: block.js,
      }));
      setComponents(comps);
      
      // Автоматически выбираем первый блок
      if (comps.length > 0) {
        setSelectedComponent(comps[0]);
      }
    } catch (error) {
      console.error("Ошибка загрузки списка блоков:", error);
    }
  };

  // Генерация JSON из компонента
  const generateJsonFromComponent = (comp: ComponentBlock): string => {
    const jsonObj = {
      component: {
        name: comp.name,
        description: comp.name,
        html: comp.html,
        css: comp.css,
        js: comp.js
      }
    };
    return JSON.stringify(jsonObj, null, 2);
  };

  // Обновление компонента из JSON
  const updateComponentFromJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      const component = parsed.component || parsed;
      
      if (selectedComponent) {
        const updatedComponent: ComponentBlock = {
          ...selectedComponent,
          name: component.name || selectedComponent.name,
          html: component.html || selectedComponent.html,
          css: component.css || selectedComponent.css,
          js: component.js || selectedComponent.js,
          json: jsonString,
        };
        
        setSelectedComponent(updatedComponent);
        setComponents(
          components.map((c) =>
            c.id === selectedComponent.id ? updatedComponent : c
          )
        );
      }
    } catch (error) {
      console.error("Ошибка парсинга JSON:", error);
      throw error;
    }
  };

  // Генерация компонента из JSON (для добавления нового блока)
  const generateFromJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      const component = parsed.component || parsed;
      
      const newComponent: ComponentBlock = {
        id: Date.now().toString(),
        name: component.name || "Новый компонент",
        html: component.html || "",
        css: component.css || "",
        js: component.js || "",
        json: jsonInput,
      };
      
      setComponents([...components, newComponent]);
      setSelectedComponent(newComponent);
      setJsonInput("");
      // Обновляем JSON редактор для нового компонента
      setComponentJson(generateJsonFromComponent(newComponent));
    } catch (error) {
      alert("Ошибка парсинга JSON. Проверьте формат данных.");
      console.error("Ошибка парсинга JSON:", error);
    }
  };

  // Обновление JSON при выборе компонента
  useEffect(() => {
    if (selectedComponent) {
      const jsonObj = {
        component: {
          name: selectedComponent.name,
          description: selectedComponent.name,
          html: selectedComponent.html,
          css: selectedComponent.css,
          js: selectedComponent.js
        }
      };
      setComponentJson(JSON.stringify(jsonObj, null, 2));
    } else {
      setComponentJson("");
    }
  }, [selectedComponent]);

  // Добавление нового пустого блока
  const addNewBlock = () => {
    const newBlock: ComponentBlock = {
      id: Date.now().toString(),
      name: `Блок ${components.length + 1}`,
      html: "<div class=\"block\">\n  <h2>Новый блок</h2>\n  <p>Содержимое блока</p>\n</div>",
      css: ".block {\n  padding: 20px;\n  margin-bottom: 20px;\n}",
      js: "// JavaScript для блока",
    };
    
    setComponents([...components, newBlock]);
    setSelectedComponent(newBlock);
  };

  // Удаление блока
  const deleteBlock = (blockId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем выбор блока при клике на удаление
    
    if (window.confirm("Удалить этот блок?")) {
      const newComponents = components.filter(c => c.id !== blockId);
      setComponents(newComponents);
      
      // Если удаляемый блок был выбран, выбираем другой или сбрасываем выбор
      if (selectedComponent?.id === blockId) {
        if (newComponents.length > 0) {
          setSelectedComponent(newComponents[0]);
        } else {
          setSelectedComponent(null);
        }
      }
    }
  };

  // Обработка drag and drop
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setComponents(items);
  };


  // Переключение «полноэкранного» режима через оверлей без использования Fullscreen API
  const togglePreviewFullscreen = () => {
    setIsPreviewFullscreen(prev => !prev);
  };

  // Функция обновления preview
  const updatePreview = useCallback(() => {
    if (!previewRef.current || components.length === 0) return;

    const iframe = previewRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

    if (iframeDoc) {
      // Объединяем все блоки с отступами
      const allHtml = components.map(comp => {
        let html = comp.html;
        // Убираем обертки если есть
        const isFullDocument = /<!doctype\s+html>/i.test(html) || /<html[\s>]/i.test(html);
        if (isFullDocument) {
          const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            html = bodyMatch[1];
          }
        }
        // Оборачиваем каждый блок в контейнер с отступом
        return `<div class="page-block" style="margin-bottom: 40px;">${html}</div>`;
      }).join('\n');

      // Объединяем все CSS
      const allCss = components.map(comp => comp.css).join('\n\n');
      
      // Объединяем все JS
      const allJs = components.map(comp => comp.js).join('\n\n');

      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html lang="ru">
          <head>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
              body {
                margin: 0;
                padding: 20px;
                font-family: system-ui, -apple-system, sans-serif;
              }
              ${allCss}
            </style>
          </head>
          <body>
            ${allHtml}
            <script>${allJs}</script>
          </body>
        </html>
      `);
      iframeDoc.close();
    }
  }, [components]);

  // Обновление preview - показываем все блоки вместе
  useEffect(() => {
    if (components.length === 0) {
      // Очищаем preview если блоков нет
      if (previewRef.current) {
        const iframe = previewRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          iframeDoc.open();
          iframeDoc.write('<html><body></body></html>');
          iframeDoc.close();
        }
      }
      return;
    }
    
    // Функция для попытки обновления preview
    const tryUpdatePreview = () => {
      if (!previewRef.current) return false;
      
      const iframe = previewRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        updatePreview();
        return true;
      }
      return false;
    };
    
    // Пробуем обновить сразу
    if (tryUpdatePreview()) {
      return;
    }
    
    // Если iframe еще не готов, ждем его загрузки
    const iframe = previewRef.current;
    if (iframe) {
      const handleLoad = () => {
        updatePreview();
      };
      
      iframe.addEventListener('load', handleLoad);
      
      // Также пробуем через небольшую задержку (несколько попыток)
      let attempts = 0;
      const maxAttempts = 10;
      
      const tryInterval = setInterval(() => {
        attempts++;
        if (tryUpdatePreview() || attempts >= maxAttempts) {
          clearInterval(tryInterval);
        }
      }, 100);
      
      return () => {
        iframe.removeEventListener('load', handleLoad);
        clearInterval(tryInterval);
      };
    }
  }, [components, updatePreview]);

  // Сохранение блока
  const handleSave = async () => {
    if (!name.trim()) {
      alert("Пожалуйста, введите название блока");
      return;
    }

    if (components.length === 0) {
      alert("Добавьте хотя бы один компонент");
      return;
    }

    setIsSaving(true);
    try {
      // Объединяем все компоненты в один блок с отступами
      const combinedHtml = components.map(comp => {
        let html = comp.html;
        const isFullDocument = /<!doctype\s+html>/i.test(html) || /<html[\s>]/i.test(html);
        if (isFullDocument) {
          const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch && bodyMatch[1]) {
            html = bodyMatch[1];
          }
        }
        return `<div class="page-block" style="margin-bottom: 40px;">${html}</div>`;
      }).join("\n");
      
      const combinedCss = `body { margin: 0; padding: 20px; font-family: system-ui, -apple-system, sans-serif; }\n\n${components.map(comp => comp.css).join("\n\n")}`;
      const combinedJs = components.map(comp => comp.js).join("\n\n");

      const blockData: PageBlock = {
        name: name.trim(),
        html: combinedHtml,
        css: combinedCss,
        js: combinedJs,
      };

      if (savedBlockId) {
        const updated = await updatePageBlock(savedBlockId, blockData);
        setSavedBlockId(updated.id);
        const shouldView = window.confirm("Блок успешно обновлен! Открыть для просмотра?");
        if (shouldView) {
          navigate(`/page/${updated.id}`);
        }
      } else {
        const created = await createPageBlock(blockData);
        setSavedBlockId(created.id);
        const shouldView = window.confirm(`Блок успешно сохранен! ID: ${created.id}\n\nОткрыть для просмотра?`);
        if (shouldView) {
          navigate(`/page/${created.id}`);
        }
      }
    } catch (error: any) {
      console.error("Ошибка сохранения блока:", error);
      alert("Ошибка при сохранении блока");
    } finally {
      setIsSaving(false);
    }
  };

  // Копирование примера JSON
  const copyJsonExample = () => {
    const jsonString = JSON.stringify(EXAMPLE_JSON, null, 2);
    navigator.clipboard.writeText(jsonString);
    alert("Пример JSON скопирован в буфер обмена!");
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {/* Верхняя панель */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-4 flex-wrap">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Название блока..."
          className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? "Сохранение..." : savedBlockId ? "Обновить" : "Сохранить"}
        </button>
        {savedBlockId && (
          <>
            <span className="text-sm text-gray-500">ID: {savedBlockId}</span>
            <button
              onClick={() => navigate(`/page/${savedBlockId}`)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Открыть
            </button>
          </>
        )}
      </div>

      {/* Основная область */}
      <div className="flex-1 flex overflow-hidden">
        {/* Левая панель - список компонентов */}
        <div className="w-64 border-r border-gray-200 bg-white flex flex-col">
          <div className="p-3 border-b border-gray-200">
            
            {/* Кнопка показа примера JSON */}
            <button
              onClick={() => setShowJsonExample(!showJsonExample)}
              className="w-full mb-2 px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              {showJsonExample ? "Скрыть" : "Показать"} пример JSON
            </button>

            {showJsonExample && (
              <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-yellow-800">Пример для GPT:</span>
                  <button
                    onClick={copyJsonExample}
                    className="px-1.5 py-0.5 bg-yellow-200 hover:bg-yellow-300 rounded text-xs"
                  >
                    Копировать
                  </button>
                </div>
                <pre className="text-xs overflow-auto max-h-32 bg-white p-1.5 rounded border mt-1">
                  {JSON.stringify(EXAMPLE_JSON, null, 2)}
                </pre>
              </div>
            )}

            {/* JSON редактор - расширяемый */}
            <div className="mb-2 flex flex-col">
              <div className="bg-gray-100 px-2 py-1 border border-gray-300 rounded-t flex items-center justify-between">
                <h3 className="text-xs font-semibold text-gray-700">
                  {selectedComponent ? `JSON: ${selectedComponent.name}` : "Вставьте JSON блока..."}
                </h3>
                {selectedComponent && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        updateComponentFromJson(componentJson);
                        alert("Компонент обновлен из JSON!");
                      } catch (error) {
                        alert("Ошибка парсинга JSON. Проверьте формат данных.");
                      }
                    }}
                    className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Применить
                  </button>
                )}
              </div>
              <div 
                ref={jsonEditorContainerRef}
                className="border-l border-r border-b border-gray-300 rounded-b overflow-hidden relative"
                style={{ 
                  minHeight: '150px',
                  maxHeight: '500px',
                  height: selectedComponent ? `${jsonEditorHeight}px` : '150px',
                  resize: 'vertical',
                  overflow: 'hidden'
                }}
                onMouseUp={() => {
                  // Обновляем высоту после изменения размера
                  if (jsonEditorContainerRef.current) {
                    const newHeight = jsonEditorContainerRef.current.offsetHeight;
                    setJsonEditorHeight(newHeight);
                  }
                }}
              >
                <div style={{ width: '100%', height: '100%' }}>
                  <Editor
                    key={selectedComponent ? `json-${selectedComponent.id}` : 'json-new'}
                    height={selectedComponent ? `${jsonEditorHeight}px` : '150px'}
                    defaultLanguage="json"
                    value={selectedComponent ? componentJson : jsonInput}
                    onChange={(value: any) => {
                      if (selectedComponent) {
                        setComponentJson(value || "");
                      } else {
                        setJsonInput(value || "");
                      }
                    }}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 12,
                      wordWrap: "on",
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                    }}
                  />
                </div>
              </div>
              {!selectedComponent && (
                <button
                  onClick={generateFromJson}
                  disabled={!jsonInput.trim()}
                  className="w-full mt-1.5 px-2 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Добавить блок из JSON
                </button>
              )}
            </div>
          </div>

          {/* Список компонентов с drag and drop */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="blocks-list">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`flex-1 overflow-y-auto p-2 ${
                    snapshot.isDraggingOver ? "bg-blue-50" : ""
                  }`}
                >
                  {components.length === 0 ? (
                    <div className="text-center text-gray-400 text-xs py-8 px-2">
                      Нет блоков.<br />
                      Нажмите "+" или добавьте блок из JSON.
                    </div>
                  ) : (
                    components.map((comp, index) => (
                      <Draggable key={comp.id} draggableId={comp.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            onClick={() => setSelectedComponent(comp)}
                            className={`p-2 mb-2 rounded cursor-pointer transition-colors relative group ${
                              selectedComponent?.id === comp.id
                                ? "bg-blue-100 border border-blue-500"
                                : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                            } ${
                              snapshot.isDragging ? "shadow-lg opacity-90" : ""
                            }`}
                          >
                            {/* Иконка перетаскивания */}
                            <div
                              {...provided.dragHandleProps}
                              className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg
                                className="w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 8h16M4 16h16"
                                />
                              </svg>
                            </div>
                            
                            {/* Кнопка удаления */}
                            <button
                              onClick={(e) => deleteBlock(comp.id, e)}
                              className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                              title="Удалить блок"
                            >
                              ×
                            </button>

                            <div className="font-medium text-xs text-gray-700 pl-5 pr-6">
                              {comp.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5 pl-5">
                              Блок {index + 1}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                  
                  {/* Кнопка добавления блока - внизу списка */}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <button
                      onClick={addNewBlock}
                      className="w-full px-3 py-2 flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      title="Добавить новый блок"
                    >
                      <span className="text-lg font-bold">+</span>
                      <span>Добавить блок</span>
                    </button>
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {/* Правая панель - редактор и preview */}
        {selectedComponent ? (
          <div className="flex-1 flex flex-col">
            {/* Заголовок с кнопками */}
            <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700">Preview (все блоки)</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAdvancedSettings(prev => !prev);
                  }}
                  className="text-gray-600 hover:text-gray-800 transition-colors px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                  title={showAdvancedSettings ? "Скрыть расширенные настройки" : "Показать расширенные настройки"}
                >
                  {showAdvancedSettings ? "Скрыть расширенные настройки" : "Расширенные настройки"}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePreviewFullscreen();
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                  title={isPreviewFullscreen ? "Выйти из полноэкранного режима" : "Открыть на полный экран"}
                >
                  {isPreviewFullscreen ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Расширенные настройки (HTML, CSS, JS) - показываются только если showAdvancedSettings = true */}
            {showAdvancedSettings && (
              <div className="flex border-b border-gray-200" style={{ height: '35%' }}>
                {/* HTML */}
                <div className="w-1/3 flex flex-col border-r border-gray-200">
                  <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-700">HTML</h3>
                  </div>
                  <div className="flex-1">
                    <Editor
                      height="100%"
                      defaultLanguage="html"
                      value={selectedComponent.html}
                      onChange={(value: any) => {
                        const updated = {
                          ...selectedComponent,
                          html: value || "",
                        };
                        setSelectedComponent(updated);
                        setComponents(
                          components.map((c) =>
                            c.id === selectedComponent.id ? updated : c
                          )
                        );
                        // Обновляем JSON при изменении HTML
                        setComponentJson(generateJsonFromComponent(updated));
                      }}
                      theme="vs-light"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        wordWrap: "on",
                      }}
                    />
                  </div>
                </div>

                {/* CSS */}
                <div className="w-1/3 flex flex-col border-r border-gray-200">
                  <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-700">CSS</h3>
                  </div>
                  <div className="flex-1">
                    <Editor
                      height="100%"
                      defaultLanguage="css"
                      value={selectedComponent.css}
                      onChange={(value: any) => {
                        const updated = {
                          ...selectedComponent,
                          css: value || "",
                        };
                        setSelectedComponent(updated);
                        setComponents(
                          components.map((c) =>
                            c.id === selectedComponent.id ? updated : c
                          )
                        );
                        // Обновляем JSON при изменении CSS
                        setComponentJson(generateJsonFromComponent(updated));
                      }}
                      theme="vs-light"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        wordWrap: "on",
                      }}
                    />
                  </div>
                </div>

                {/* JavaScript */}
                <div className="w-1/3 flex flex-col">
                  <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-700">JavaScript</h3>
                  </div>
                  <div className="flex-1">
                    <Editor
                      height="100%"
                      defaultLanguage="javascript"
                      value={selectedComponent.js}
                      onChange={(value: any) => {
                        const updated = {
                          ...selectedComponent,
                          js: value || "",
                        };
                        setSelectedComponent(updated);
                        setComponents(
                          components.map((c) =>
                            c.id === selectedComponent.id ? updated : c
                          )
                        );
                        // Обновляем JSON при изменении JS
                        setComponentJson(generateJsonFromComponent(updated));
                      }}
                      theme="vs-light"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        wordWrap: "on",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Preview - показывает все блоки вместе */}
            <div 
              ref={previewContainerRef}
              className={`bg-white ${isPreviewFullscreen ? "fixed inset-0 z-50 flex flex-col" : "flex flex-col"}`}
              style={{ 
                flex: showAdvancedSettings ? '1 1 30%' : '1 1 50%',
                minHeight: 0
              }}
            >
              <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
                <iframe
                  ref={previewRef}
                  className="w-full h-full border-0"
                  title="Preview"
                  onLoad={() => {
                    // Обновляем preview когда iframe загрузился
                    if (components.length > 0) {
                      setTimeout(() => {
                        updatePreview();
                      }, 50);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Выберите блок для редактирования или добавьте новый блок
          </div>
        )}
      </div>
    </div>
  );
}

export default PageEditorV2;

